/**
 * Бейдж, статус и флаг приоритета.
 *
 * Статус кодируется тремя каналами сразу — цветом, формой и текстом
 * (docs/04, §1.6): цвет один не работает ни при дальтонизме, ни в печати.
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/cn";

const badgeVariants = cva(
  "inline-flex items-center gap-[var(--pt-space-4)] rounded-[var(--pt-radius-2)] " +
    "px-[var(--pt-space-6)] font-medium whitespace-nowrap " +
    "text-[var(--pt-fs-xs)] leading-[var(--pt-lh-xs)] h-[18px]",
  {
    variants: {
      tone: {
        neutral: "bg-[var(--pt-bg-sunken)] text-[var(--pt-text-secondary)]",
        accent: "bg-[var(--pt-accent-soft)] text-[var(--pt-accent-text)]",
        success: "bg-[var(--pt-success-soft)] text-[var(--pt-success-text)]",
        warning: "bg-[var(--pt-warning-soft)] text-[var(--pt-warning-text)]",
        danger: "bg-[var(--pt-danger-soft)] text-[var(--pt-danger-text)]",
        info: "bg-[var(--pt-info-soft)] text-[var(--pt-info-text)]",
      },
      outlined: { true: "bg-transparent border" },
    },
    compoundVariants: [
      { tone: "neutral", outlined: true, class: "border-[var(--pt-border-default)]" },
      { tone: "accent", outlined: true, class: "border-[var(--pt-accent-border)]" },
      { tone: "success", outlined: true, class: "border-[var(--pt-success-border)]" },
      { tone: "warning", outlined: true, class: "border-[var(--pt-warning-border)]" },
      { tone: "danger", outlined: true, class: "border-[var(--pt-danger-border)]" },
      { tone: "info", outlined: true, class: "border-[var(--pt-info-border)]" },
    ],
    defaultVariants: { tone: "neutral" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, outlined, ...props }: BadgeProps): React.ReactElement {
  return <span className={cn(badgeVariants({ tone, outlined }), className)} {...props} />;
}

export type StatusKind = "new" | "in_progress" | "done" | "rejected";

const STATUS_MARKER: Record<StatusKind, string> = {
  new: "◇",
  in_progress: "◆",
  done: "✓",
  rejected: "✕",
};

const STATUS_TONE: Record<StatusKind, NonNullable<BadgeProps["tone"]>> = {
  new: "neutral",
  in_progress: "accent",
  done: "success",
  rejected: "danger",
};

/** Цвета, которые настройщик выбирает статусу. Имена совпадают с тонами значка. */
const STATUS_COLORS = new Set(["neutral", "accent", "info", "success", "warning", "danger"]);

/**
 * Тон значка статуса.
 *
 * Цвет выбирает настройщик, и он главнее вида: «На проверке» и «У клиента» —
 * оба «в работе», но различать их на доске человек будет по цвету. Вид
 * остаётся запасным вариантом для статусов, заведённых до появления выбора.
 */
export function statusTone(kind: StatusKind, color?: string): NonNullable<BadgeProps["tone"]> {
  if (color !== undefined && STATUS_COLORS.has(color)) {
    return color as NonNullable<BadgeProps["tone"]>;
  }
  return STATUS_TONE[kind];
}

/**
 * Цвет точки, которой статус помечают там, где значок не помещается:
 * в полосе отбора, в заголовке колонки доски, в списке настроек.
 *
 * «Серый» — это отсутствие цвета, и берётся он от текста, а не от палитры:
 * собственного нейтрального тона у оформления нет.
 */
export function statusDot(color: string | undefined): string {
  if (color === undefined || !STATUS_COLORS.has(color) || color === "neutral") {
    return "var(--pt-text-tertiary)";
  }
  return `var(--pt-${color})`;
}

export interface StatusChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  kind: StatusKind;
  /** Название статуса из метамодели: «В работе», «Согласовано», «У клиента». */
  label: string;
  /** Цвет из настроек типа задач. Без него значок красится по виду. */
  color?: string | undefined;
}

export function StatusChip({
  kind,
  label,
  color,
  className,
  ...props
}: StatusChipProps): React.ReactElement {
  return (
    <Badge
      tone={statusTone(kind, color)}
      className={cn("gap-[var(--pt-space-4)]", className)}
      {...props}
    >
      <span aria-hidden>{STATUS_MARKER[kind]}</span>
      {label}
    </Badge>
  );
}

export type Priority = 0 | 1 | 2 | 3;

const PRIORITY_LABEL: Record<Priority, string> = {
  0: "Без приоритета",
  1: "Обычный",
  2: "Высокий",
  3: "Срочный",
};

const PRIORITY_CLASS: Record<Priority, string> = {
  0: "text-[var(--pt-text-tertiary)]",
  1: "text-[var(--pt-text-secondary)]",
  2: "text-[var(--pt-warning-text)]",
  3: "text-[var(--pt-danger-text)]",
};

export interface PriorityFlagProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: Priority;
}

export function PriorityFlag({
  value,
  className,
  ...props
}: PriorityFlagProps): React.ReactElement {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-[var(--pt-space-2)] text-[var(--pt-fs-xs)]",
        PRIORITY_CLASS[value],
        className,
      )}
      title={PRIORITY_LABEL[value]}
      {...props}
    >
      <span aria-hidden>{"!".repeat(value) || "·"}</span>
      <span className="sr-only">{PRIORITY_LABEL[value]}</span>
    </span>
  );
}
